import dayjs from 'dayjs';

export const ISO_DATE = 'YYYY-MM-DD';

/** How far a value may sit from the median before the grid flags it. */
export const ANOMALY_TOLERANCE = 0.35;

/** Parses a typed quantity, tolerating the thousands separators people paste from Excel. */
export function parseQuantity (value) {
    if (value === null || value === undefined) return null;

    const text = String(value).replace(/[\s,]/g, '');
    if (text === '') return null;

    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : NaN;
}

export function formatQuantity (value, maximumFractionDigits = 3) {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    return Number(value).toLocaleString(undefined, { maximumFractionDigits });
}

/** Days in an inclusive period, or null when either end is missing. */
export function periodDays (start, end) {
    if (!start || !end) return null;
    return dayjs(end).diff(dayjs(start), 'day') + 1;
}

/** The twelve calendar months of a year as inclusive ISO period pairs. */
export function monthsOfYear (year) {
    return Array.from({ length: 12 }, (_, month) => {
        const first = dayjs(new Date(year, month, 1));
        return {
            month,
            label: first.format('MMMM'),
            periodStart: first.format(ISO_DATE),
            periodEnd: first.endOf('month').format(ISO_DATE),
        };
    });
}

/**
 * Median of the per-day rates, used as the anomaly baseline. The median rather than the mean so a
 * single spike — the leak or the misplaced decimal you are looking for — does not hide itself.
 */
export function medianRate (rows) {
    const rates = rows
        .map(row => {
            const days = periodDays(row.periodStart, row.periodEnd);
            const quantity = parseQuantity(row.consumption ?? row.quantity);
            return days && days > 0 && quantity !== null && !Number.isNaN(quantity) ? quantity / days : null;
        })
        .filter(rate => rate !== null)
        .sort((a, b) => a - b);

    if (rates.length < 3) return null;

    const middle = Math.floor(rates.length / 2);
    return rates.length % 2 === 0 ? (rates[middle - 1] + rates[middle]) / 2 : rates[middle];
}

/** How far this row's per-day rate sits from the median, as a fraction, or null when unknowable. */
export function rateDeviation (row, median, field) {
    if (!median) return null;

    const days = periodDays(row.periodStart, row.periodEnd);
    const quantity = parseQuantity(row[field]);
    if (!days || days <= 0 || quantity === null || Number.isNaN(quantity)) return null;

    return (quantity / days - median) / median;
}

/**
 * Problems with the periods themselves, keyed by row key: overlaps, and the gap before a row.
 * The server enforces overlaps too; this is so the user sees it before they save.
 */
export function periodIssues (rows) {
    const issues = {};
    const ordered = rows
        .filter(row => row.periodStart && row.periodEnd)
        .slice()
        .sort((a, b) => a.periodStart.localeCompare(b.periodStart));

    ordered.forEach((row, index) => {
        if (dayjs(row.periodEnd).isBefore(dayjs(row.periodStart))) {
            issues[row.key] = { type: 'outOfOrder' };
            return;
        }

        if (index === 0) return;

        const previous = ordered[index - 1];
        if (row.periodStart <= previous.periodEnd) {
            issues[row.key] = { type: 'overlap' };
            return;
        }

        const gap = dayjs(row.periodStart).diff(dayjs(previous.periodEnd), 'day') - 1;
        if (gap > 0) {
            issues[row.key] = { type: 'gap', days: gap };
        }
    });

    return issues;
}

/** True when anything on the row differs from what was last saved. */
export function isRowDirty (row) {
    if (!row.saved) return true;
    return Object.keys(row.saved).some(field => (row[field] ?? '') !== (row.saved[field] ?? ''));
}

/** A stable key for a row that may not have a server id yet. */
let nextKey = 1;
export function newRowKey () {
    nextKey += 1;
    return `new-${nextKey}`;
}
